/* istanbul ignore file */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import _ from 'lodash';
import { scrollItemIntoView } from '../common/utils';
import { Dropdown } from './dropDown';

export function getSelectableItem(list = [], cursor, initialCursor, nextFn) {
    try {
        let nextCursor = nextFn(cursor);
        if (nextCursor === list.length || nextCursor < 0) {
            // we have reached the end of the line without finding the next/previous cursor.
            // don't cross the bounds
            return initialCursor;
        }
        if (!list[nextCursor]) {
            // when index not found
            return initialCursor; // noop
        }
        const { key, disabled } = list[nextCursor];
        const isDisabled = disabled || key === '_NEXT_ROW' || key === '_LIST_SEPARATOR';
        if (isDisabled) {
            return getSelectableItem(list, nextCursor, initialCursor, nextFn);
        }
        return nextCursor;
    }
    catch (e) {
        // console.log(e);
    }
    return initialCursor;
}

// a wrapper function to make drop downs navigable with keyboard arrows
// DDComp can be used to provide a custom dropdown component but it needs to have required contract
// 1. DDComp needs to implement cursor, onHovered, onDropdownOpen and onDropdownClose behaviors
// 2. need have required classnames for container `js-navigable-menu-container-${id}` and each menu item `js-navigable-item-${id}-${current}`
// 3, listOptions must be an array
export function NavigableDropdown(props) {
    const {
        value,
        listOptions = [],
        id,
        DDComp = Dropdown,
        filterListOptions,
        recordKey,
        isMultiSelect,
        selectedKeysProp = 'selectedKeys',
        selectedKeysManagedByComp = false,
        submitOnClose,
        optimizeRendering,
        onSelectFunc = 'onSelect',
        testProps = {}, // only for testing
        adjustMenuPosition
    } = props;
    const ddRef = useRef(null);
    const menuListRef = testProps.menuListRef || useRef(null); // eslint-disable-line
    const compRef = useRef(null);
    let selectedItems = _.get(props, selectedKeysProp);

    if (selectedKeysManagedByComp) {
        const compSelectedKeys = _.get(ddRef, selectedKeysManagedByComp);
        if (!_.isEmpty(compSelectedKeys)) {
            if (_.isObject(compSelectedKeys)) {
                selectedItems = _.keys(_.pickBy(compSelectedKeys, value => value));
            }
            else {
                selectedItems = compSelectedKeys;
            }
        }
    }

    const [downPress, setDownPressed] = useState(0);
    const [upPress, setUpPressed] = useState(0);
    const [enterPress, setEnterPressed] = useState(false);
    const [hovered, setHovered] = useState(undefined);
    const [isOpen, setOpened] = useState(false);
    const [showSelected, setShowSelected] = useState(false);

    // Some times we get object key-value pairs instead of array of objects for listOptions. 
    // So we convert it to array and memoize it.
    const memoizedListOptions = useMemo(() => {
        if (_.isArray(listOptions)) {
            return listOptions;
        }
        const arrayListOptions = _.map(listOptions, (label, key) => {
            return { key, label };
        });
        //listOptions has keys sorted, and mapping doesn't gurantee that order
        return _.sortBy(arrayListOptions, (obj) => obj.key);
    }, [listOptions]);
    let filteredObjects = memoizedListOptions;
    let filteredListOptions;

    if (isMultiSelect) {
        filteredObjects = listOptions;

        // 1. pick only selected items
        if (showSelected) {
            if (_.isArray(listOptions)) {
                filteredObjects = _.filter(listOptions, ({ key }) => _.includes(selectedItems, key));
            }
            else {
                filteredObjects = _.pickBy(listOptions, (label, key) => _.includes(selectedItems, key));
            }
        }

        // 2. filter as per the search text
        filteredListOptions = !_.isNil(filterListOptions) ? filterListOptions(filteredObjects) : filteredObjects;
        // 3. make it an array if it is not an array already so it plays nice with navigable dropdown logic
        if (!_.isArray(filteredListOptions)) {
            filteredListOptions = _.map(filteredListOptions, (label, key) => {
                return { key, label };
            });
            //filteredListOptions has keys sorted, and mapping doesn't gurantee that order
            filteredListOptions = _.sortBy(filteredListOptions, (obj) => obj.key);
        }
    }
    else {
        // filter as per the search text
        filteredListOptions = !_.isNil(filterListOptions) ? filterListOptions(filteredObjects) : filteredObjects;
    }

    const initialCursor = _.findIndex(filteredListOptions, { key: value });
    const [cursor, setCursor] = useState(initialCursor ? initialCursor : 0);
    const downHandler = (event) => {
        if (!isOpen)
            return;
        const { key } = event;
        if (key === 'ArrowDown') {
            event.preventDefault();
            // we just update the number over and over again to handle long press action
            setDownPressed(oldState => oldState + 1);
        }
        else if (key === 'ArrowUp') {
            event.preventDefault();
            setUpPressed(oldState => oldState + 1);
        }
        // else if (key === 'Enter' || key === ' ') {
        else if (key === 'Enter') {
            event.preventDefault();
            setEnterPressed(true);
        }
    }

    const upHandler = (event) => {
        if (!isOpen)
            return;
        const { key } = event;
        if (key === 'ArrowDown') {
            setDownPressed(0);
        }
        else if (key === 'ArrowUp') {
            setUpPressed(0);
        }
        // else if (key === 'Enter' || key === ' ') {
        else if (key === 'Enter') {
            setEnterPressed(false);
        }
    };

    // whenever list options size changes (could be due to filter), reset the current cursor to top
    // or whenever user toggles between selected and all items
    useEffect(() => {
        setCursor(0);
    }, [filteredListOptions.length, showSelected]);

    //If adjustMenuPosition is passed as a prop , then call that function,
    //whenever list options size changes
    useEffect(() => {
        if (_.isFunction(adjustMenuPosition) && ddRef.current)
            adjustMenuPosition(ddRef.current);
    }, [filteredListOptions.length, adjustMenuPosition]);

    // when dropdown is opened
    useEffect(() => {
        if (isOpen) {
            scrollToCurrentItem();
            document.addEventListener('keydown', downHandler);
            document.addEventListener('keyup', upHandler);
        }
        else {
            document.removeEventListener('keydown', downHandler);
            document.removeEventListener('keyup', upHandler);
        }
        return () => {
            document.removeEventListener('keydown', downHandler);
            document.removeEventListener('keyup', upHandler);
        };
    }, [isOpen]); // eslint-disable-line

    // when down arrow pressed
    useEffect(() => {
        if (filteredListOptions.length && downPress) {
            setCursor(prevState => getSelectableItem(filteredListOptions, prevState, prevState, (next) => next + 1));
        }
    }, [downPress]); // eslint-disable-line

    // when up arrow pressed
    useEffect(() => {
        if (filteredListOptions.length && upPress) {
            setCursor(prevState => getSelectableItem(filteredListOptions, prevState, prevState, (prev) => prev - 1));
        }
    }, [upPress]); // eslint-disable-line

    // whenever user tried to navigate with keyboard arrows
    useEffect(() => {
        // make sure the item is in view and if not scroll to the item
        scrollToCurrentItem();
    }, [downPress, upPress]);// eslint-disable-line

    const scrollToCurrentItem = () => {
        const current = _.isNil(cursor) ? value : cursor;
        if (!_.isNil(current)) {
            if (optimizeRendering) {
                // react-window FixedList and we need to use its api to scroll to certain item
                try {
                    menuListRef.current && menuListRef.current.scrollToItem(current);
                }
                catch (e) { } // eslint-disable-line no-empty
            }
            else {
                scrollItemIntoView(`js-navigable-menu-container-${_.kebabCase(id)}`, `js-navigable-item-${_.kebabCase(id)}-${current}`);
            }
        }
    }

    useEffect(() => {
        if (filteredListOptions.length && enterPress) {
            // an alternative logic for multi select as we do not close the dropdown
            if (isMultiSelect) {
                const onSubmit = props[onSelectFunc];
                const currentKey = filteredListOptions[cursor].key;
                const isSelected = _.includes(selectedItems, currentKey);

                if (submitOnClose) {
                    const refUsed = compRef.current ? compRef : ddRef;
                    if (_.chain(refUsed).get(`current.${onSelectFunc}`).isFunction().value())
                        refUsed.current[onSelectFunc](currentKey);
                }
                else {
                    onSubmit(currentKey, !isSelected);
                }
            }
            else {
                const onSubmit = props[onSelectFunc];
                onSubmit(filteredListOptions[cursor].key, recordKey);
                setTimeout(() => {
                    if (_.chain(ddRef).get('current.closeDropdown').isFunction().value())
                        ddRef.current.closeDropdown();
                }, 100);
                // setEnterPressed(false); TODO: verify this
            }
            setEnterPressed(false);
        }
    }, [cursor, enterPress]); // eslint-disable-line

    useEffect(() => {
        if (filteredListOptions.length && !_.isNil(hovered)) {
            setCursor(hovered);
        }
    }, [hovered]); // eslint-disable-line

    useEffect(() => {
        scrollToCurrentItem();
    }, []); // eslint-disable-line

    // when dropdown is opened, highlight the previously selected item and scroll to it
    const onDropdownOpen = (...args) => {
        setOpened(true);
        scrollToCurrentItem();
        if (_.isFunction(props.onDropdownOpen)) {
            props.onDropdownOpen(...args);
        }
    };

    const onDropdownClose = (...args) => {
        setOpened(false);
        if (_.isFunction(props.onDropdownClose)) {
            props.onDropdownClose(...args);
        }
    };

    return (
        <DDComp
            ref={ddRef}
            {...props}
            handleNavKeys={false}

            cursor={cursor}
            onHovered={setHovered}
            onDropdownOpen={onDropdownOpen}
            onDropdownClose={onDropdownClose}
            onShowSelected={setShowSelected}
            menuListRef={menuListRef}
            compRef={compRef}
        />
    );
}
NavigableDropdown.displayName = 'NavigableDropdown';

